import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, X, Facebook, Instagram, Youtube } from 'lucide-react';

// Custom TikTok Icon Component
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Custom Google My Business Icon Component  
const GoogleIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
  </svg>
);

// Custom WhatsApp Icon Component
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const SocialFollowWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  const socialLinks = [
    {
      name: 'Google My Business',
      icon: <GoogleIcon size={24} />,
      url: 'https://g.co/kgs/2HAoiPV',
      color: 'hover:bg-red-500',
      delay: 0.1
    },
    {
      name: 'Facebook',
      icon: <Facebook size={24} />,
      url: 'https://www.facebook.com/Auto.carparts.bilaljung.lahore',
      color: 'hover:bg-blue-600',
      delay: 0.15
    },
    {
      name: 'Instagram',
      icon: <Instagram size={24} />,
      url: 'https://www.instagram.com/bilalganj_parts',
      color: 'hover:bg-pink-600',
      delay: 0.2
    },
    {
      name: 'TikTok',
      icon: <TikTokIcon size={24} />,
      url: 'https://www.tiktok.com/@bilalganjautoparts',
      color: 'hover:bg-black',
      delay: 0.25
    },
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon size={24} />,
      url: 'https://whatsapp.com/channel/0029Vagd3wA47Xe9Qkj8X50T',
      color: 'hover:bg-green-500',
      delay: 0.3
    },
    {
      name: 'YouTube',
      icon: <Youtube size={24} />,
      url: 'https://www.youtube.com/@bilalganjautoparts',
      color: 'hover:bg-red-600',
      delay: 0.35
    }
  ];

  // Calculate positions for half-circle arc on the left
  const getIconPosition = (index, total) => {
    const radius = 140; // Distance from main button
    const startAngle = 270; // Start angle (top)
    const endAngle = 90; // End angle (bottom) - going counterclockwise on left
    const angleStep = (endAngle - startAngle) / (total - 1);
    const angle = (startAngle + angleStep * index) * (Math.PI / 180);
    
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40">
      {/* Social Media Icons in Half-Circle */}
      <AnimatePresence>
        {isOpen && (
          <>
            {socialLinks.map((social, index) => {
              const position = getIconPosition(index, socialLinks.length);
              
              return (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ 
                    opacity: 0, 
                    scale: 0,
                    x: 0,
                    y: 0
                  }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    x: position.x,
                    y: position.y
                  }}
                  exit={{ 
                    opacity: 0, 
                    scale: 0,
                    x: 0,
                    y: 0
                  }}
                  transition={{ 
                    delay: social.delay,
                    type: 'spring',
                    stiffness: 260,
                    damping: 20
                  }}
                  whileHover={{ 
                    scale: 1.2,
                    rotate: 10,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.9 }}
                  className={`
                    absolute w-14 h-14 rounded-full bg-white border-2 border-primary/30
                    flex items-center justify-center
                    text-gray-700 transition-all duration-300
                    shadow-xl hover:shadow-2xl hover:border-primary hover:text-white
                    ${social.color}
                    backdrop-blur-sm
                  `}
                  style={{
                    left: '50%',
                    top: '50%',
                    transform: `translate(-50%, -50%)`
                  }}
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              );
            })}
          </>
        )}
      </AnimatePresence>

      {/* Main Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative w-16 h-16 rounded-full 
          ${isOpen ? 'bg-red-500 hover:bg-red-600' : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'}
          text-white
          flex items-center justify-center
          shadow-xl hover:shadow-2xl
          transition-all duration-300
          border-2 border-white/30
          overflow-hidden
        `}
        whileHover={{ 
          scale: 1.1
        }}
        whileTap={{ scale: 0.95 }}
        animate={{
          rotate: isOpen ? 180 : 0
        }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        {/* Pulse Animation Ring */}
        {!isOpen && (
          <motion.div
            className="absolute inset-0 rounded-full bg-orange-500"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.6, 0, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        )}
        
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={28} strokeWidth={2.5} />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-center"
            >
              <Share2 size={28} strokeWidth={2.5} />
              <span className="text-[10px] font-bold mt-0.5">FOLLOW</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Follow Us Text Tooltip for Desktop */}
      {!isOpen && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden md:block absolute left-full ml-3 top-1/2 -translate-y-1/2 
                     bg-gradient-to-r from-orange-500 to-red-600 rounded-lg px-4 py-2
                     text-sm font-bold text-white shadow-xl
                     whitespace-nowrap pointer-events-none
                     backdrop-blur-sm"
        >
          Follow Us on Social Media
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full
                          w-0 h-0 border-t-8 border-b-8 border-r-8
                          border-transparent border-r-orange-500" />
        </motion.div>
      )}
    </div>
  );
};

export default SocialFollowWidget;
